import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { faQuestion } from '@fortawesome/pro-solid-svg-icons/faQuestion';
import { createFileRoute } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import useResponsive from 'ahooks/es/useResponsive';
import { type PropsWithChildren, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { ReportError, useQuery } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import FontAwesomeIcon from '#/components/FontAwesomeIcon.tsx';
import { isDealersMeetup, isGameTime } from '#/schedule/isEventType.ts';
import { useInstancePreferences } from '@/components/__app/InstancePreferenceContext.tsx';
import AutoJoinInfoModal from '@/components/_event/AutoJoinInfoModal.tsx';
import EventData from '@/components/_event/EventData.tsx';
import RoomInstances from '@/components/_event/RoomInstances.tsx';
import EventPrimaryActionButton from '@/components/EventPrimaryActionButton/index.tsx';
import LinkButton from '@/components/LinkButton.tsx';
import { ExploreLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import useEventReferrer, { type EventReference } from '@/lib/helpers/useEventReferrer.ts';
import useFlag from '@/lib/hooks/useFlag.ts';
import type TranslationKey from '@/lib/i18n/TranslationKey.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';
import useTitle from '@/lib/useTitle.ts';

const getPrevRoute = (prevPath: EventReference): TranslationKey<['common', 'explore']> => {
  if (prevPath.link.to === '/explore') {
    return 'shared.navigation.explore';
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  switch (prevPath.link.to!.split('/').pop()) {
    case 'games':
      return 'explore:games';
    case 'meetups':
      return 'explore:meetups';
    case 'club-fynn':
      return 'explore:club_fynn';
    case 'lobby':
      return 'explore:lobby';
    case 'events':
      return 'explore:events';
    case 'dealers':
      return 'explore:dealers';
    default:
      return 'shared.navigation.schedule';
  }
};

const Contain = ({ children }: PropsWithChildren) => (
  <Container style={{ zIndex: 1 }} className="mt-4">
    {children}
  </Container>
);

const Event = () => {
  const { id: eventId } = Route.useParams();

  const instancePrefs = useInstancePreferences();

  const [showAutoJoinInfoModal, setShowAutoJoinInfoModal] = useState(false);

  // TODO: should this depend on explore
  const { t } = useTranslation(['common', 'event', 'explore']);

  const event = useQuery({
    queryKey: [QUERY_KEYS.Event, eventId],
    queryFn: async () => {
      const response = await fox.value.eventFindOneV3({
        params: {
          eventId: eventId,
        },
      });
      switch (response.status) {
        case 200:
          return response.data;
        case 404:
          return null;
        default:
          throw new ReportError(response);
      }
    },
  });

  // TODO maybe a better way to hide the instances of these pseudo events.
  // maybe we can guarantee there's no instances for them?
  const isGameTimePowerHour = isGameTime(eventId);
  const isDealerDenMeetup = isDealersMeetup(eventId);
  const isNormalEvent = !isGameTimePowerHour && !isDealerDenMeetup;

  const { lg } = useResponsive();
  const buttonSize = lg ? 'xl' : 'lg';

  const featuresReady = useFlagsStatus().flagsReady;
  const enableDealers = useFlag('dealersDen');
  const enableEventDetails = useFlag('eventDetails');

  const [eventReferrer] = useEventReferrer();

  // CON-SPECIFIC: references dealer's den event id
  const previousPath: EventReference =
    (eventId.startsWith('dealers-den') && !enableDealers) ||
    eventId === 'lobby' ||
    eventId === 'fireworks'
      ? {
          link: {
            to: '/explore',
          },
        }
      : (eventReferrer ?? { link: { to: '/schedule' } });

  useTitle('shared.navigation.event', event.data?.name);

  if (!featuresReady || event.isLoading) {
    return <PageLoading />;
  }

  if (!enableEventDetails) {
    return <PageDisabled absolutelyCentered />;
  }

  if (event.data === null || event.isError || !event.data) {
    return (
      <Contain>
        <Row className="pb-4 px-4 px-sm-0">
          <Col lg={5} xs={12} className="d-flex align-items-center">
            <LinkButton
              {...previousPath.link}
              className="mr-3 mr-md-4"
              variant="outline-white"
              size={buttonSize}
              title={t('event:return_to', { page: t(getPrevRoute(previousPath)) })}
            >
              <FontAwesomeIcon icon={faChevronLeft} size="lg" />
            </LinkButton>
            <h1 className="my-0">{t('event:details')}</h1>
          </Col>
        </Row>
        <section
          className="mt-4 p-4 rounded-lg text-center"
          style={{
            backgroundColor: 'rgba(0, 54, 120, 0.925)',
            boxShadow: '0px 8px 8px rgba(0,0,0,0.2)',
          }}
        >
          {t('event:event_not_found')}
        </section>
      </Contain>
    );
  }

  return (
    <Contain>
      <AutoJoinInfoModal
        show={showAutoJoinInfoModal}
        onClose={() => setShowAutoJoinInfoModal(false)}
      />
      <Row className="pb-4 px-4 px-sm-0">
        <Col lg={5} xs={12} className="d-flex align-items-center">
          <LinkButton
            {...previousPath.link}
            className="mr-3 mr-md-4"
            variant="outline-white"
            size={buttonSize}
            title={t('event:return_to', { page: t(getPrevRoute(previousPath)) })}
          >
            <FontAwesomeIcon icon={faChevronLeft} size="lg" />
          </LinkButton>
          <h1 className="my-0">{t('event:details')}</h1>
        </Col>
        <Col
          lg={7}
          xs={12}
          className="d-flex justify-content-between justify-content-lg-end flex-column flex-lg-row mt-4 mt-lg-0"
        >
          <div className="d-flex align-items-center ">
            {event.data.status !== 'Closed' && (
              <Button
                variant="dark"
                size={buttonSize}
                icon={<FontAwesomeIcon icon={faQuestion} />}
                rounded
                onClick={() => setShowAutoJoinInfoModal(true)}
                className="flex-shrink-0 ml-0 ml-md-3 shadow-lg"
              />
            )}
            <Button
              variant="dark"
              size={buttonSize}
              icon={<FontAwesomeIcon icon={faGear} />}
              rounded
              onClick={() => instancePrefs.show()}
              className="flex-shrink-0 ml-0 ml-md-3 mr-0 mr-md-3 shadow-lg"
            />
            <EventPrimaryActionButton
              size={buttonSize}
              event={event.data}
              className="ml-2 w-100"
              onRSVPSuccess={() => event.refetch()}
            />
          </div>
        </Col>
      </Row>

      <EventData event={event.data} />
      {event.data.instances.length > 0 && isNormalEvent && <RoomInstances event={event.data} />}
    </Contain>
  );
};

export const Route = createFileRoute('/explore/event/$id')({
  component: () => (
    <ExploreLayout title="shared.navigation.event">
      <Event />
    </ExploreLayout>
  ),
});
